import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { MOCK_ACTIVITY_FEED } from 'data/mockData';
import { ROUTES } from 'config/navConfig';
import { useRole } from 'context/RoleContext';

const TYPE_TO_ROUTE = {
  deal:       ROUTES.pipeline,
  ic:         ROUTES.icSubmission,
  asset:      ROUTES.portfolioAssets,
  document:   ROUTES.dataRoom,
  compliance: ROUTES.auditTrail,
  user:       ROUTES.teamManagement,
  spv:        ROUTES.spvDashboard,
  system:     null,
};

const ACTIVITY_TYPES = {
  deal:       { icon: 'Briefcase',      color: 'var(--color-primary)',   bg: 'rgba(59,76,76,0.08)'    },
  ic:         { icon: 'ClipboardCheck', color: '#6366f1',                bg: 'rgba(99,102,241,0.08)'  },
  asset:      { icon: 'Building2',      color: '#0891b2',                bg: 'rgba(8,145,178,0.08)'   },
  document:   { icon: 'FileText',       color: '#d97706',                bg: 'rgba(217,119,6,0.08)'   },
  compliance: { icon: 'ShieldCheck',    color: '#059669',                bg: 'rgba(5,150,105,0.08)'   },
  user:       { icon: 'Users',          color: '#7c3aed',                bg: 'rgba(124,58,237,0.08)'  },
  spv:        { icon: 'Layers',         color: '#0f766e',                bg: 'rgba(15,118,110,0.08)'  },
  system:     { icon: 'Settings',       color: '#94a3b8',                bg: 'rgba(148,163,184,0.08)' },
};

const FILTER_OPTIONS = [
  { key: 'all',        label: 'All Activity' },
  { key: 'deal',       label: 'Deals' },
  { key: 'ic',         label: 'IC' },
  { key: 'asset',      label: 'Assets' },
  { key: 'document',   label: 'Documents' },
  { key: 'compliance', label: 'Compliance' },
];

const DEADLINE_TAGS = ['IC Review', 'Capital Call', 'Q1 Review'];

const QUICK_NAV = [
  { label: 'IC Submissions', icon: 'ClipboardList', path: ROUTES.icSubmission },
  { label: 'Pipeline',       icon: 'GitBranch',     path: ROUTES.pipeline },
  { label: 'Portfolio Assets', icon: 'PieChart',    path: ROUTES.portfolioAssets },
];

function ActivityCard({ item, isExpanded, onToggle }) {
  const navigate = useNavigate();
  const type = ACTIVITY_TYPES[item.type] ?? ACTIVITY_TYPES.system;
  const dest = item.path ?? TYPE_TO_ROUTE[item.type] ?? null;

  return (
    <div
      className={`card mb-3 overflow-hidden transition-all duration-200 cursor-pointer group ${
        isExpanded ? 'border-primary/25 shadow-sm' : 'hover:border-primary/20'
      }`}
      onClick={onToggle}
    >
      {/* Card header */}
      <div className="flex items-start gap-3 p-4">
        <div
          className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5"
          style={{ background: type.bg }}
        >
          <Icon name={type.icon} size={16} color={type.color} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="font-caption text-sm font-semibold text-foreground leading-snug">
              {item.title}
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="font-caption text-xs text-muted-foreground whitespace-nowrap">{item.time}</span>
              <Icon
                name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                size={14}
                color="var(--color-muted-foreground)"
              />
            </div>
          </div>

          {/* Always-visible preview */}
          {!isExpanded && (
            <p className="font-caption text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-1">
              {item.description}
            </p>
          )}

          {/* Actor */}
          {item.actor && !isExpanded && (
            <p className="font-caption text-xs text-muted-foreground mt-1 opacity-70">{item.actor}</p>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div
          className="px-4 pb-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-t border-border pt-3 mt-0">
            <p className="font-caption text-sm text-foreground leading-relaxed">
              {item.description}
            </p>

            {item.actor && (
              <p className="font-caption text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <Icon name="User" size={11} color="var(--color-muted-foreground)" />
                {item.actor}
              </p>
            )}

            {item.tags && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 rounded-full text-xs font-caption font-medium"
                    style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {dest && (
              <div className="mt-4 flex items-center justify-end">
                <Button
                  variant="default"
                  size="sm"
                  iconName="ArrowRight"
                  iconPosition="right"
                  iconSize={13}
                  onClick={() => navigate(dest)}
                >
                  View
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ActivityFeedPage() {
  const navigate = useNavigate();
  const { role, activeContext } = useRole();
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const contextFiltered = useMemo(() => MOCK_ACTIVITY_FEED.filter((item) => {
    if (item.roleTarget && !item.roleTarget.includes(role)) return false;
    if (item.contextIds && !item.contextIds.includes(activeContext?.id)) return false;
    return true;
  }), [role, activeContext]);

  // Count per type for filter pills
  const typeCounts = useMemo(() => FILTER_OPTIONS.reduce((acc, f) => {
    acc[f.key] = f.key === 'all'
      ? contextFiltered.length
      : contextFiltered.filter((a) => a.type === f.key).length;
    return acc;
  }, {}), [contextFiltered]);

  const newItemCount = useMemo(() => contextFiltered.filter((i) => i.dateLabel === 'Today').length, [contextFiltered]);

  const filtered = filter === 'all'
    ? contextFiltered
    : contextFiltered.filter((a) => a.type === filter);

  const grouped = filtered.reduce((acc, item) => {
    const g = acc.find((x) => x.date === item.dateLabel);
    if (g) { g.items.push(item); }
    else    { acc.push({ date: item.dateLabel, items: [item] }); }
    return acc;
  }, []);

  // Right panel — deadline items
  const deadlineItems = contextFiltered
    .filter((item) => item.tags?.some((t) => DEADLINE_TAGS.includes(t)))
    .slice(0, 4);

  const todayCount = contextFiltered.filter((i) => i.dateLabel === 'Today').length;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Sticky header */}
        <div
          className="sticky top-0 px-6 md:px-10 lg:px-12 py-4 border-b border-border bg-background"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'var(--color-primary)' }}
              >
                <Icon name="Activity" size={18} color="white" />
              </div>
              <div>
                <h1 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.375rem', lineHeight: 1.3 }}>
                  Activity Feed
                </h1>
                <p className="font-caption text-xs text-muted-foreground">
                  Platform-wide deal, asset, and compliance activity
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs font-caption text-muted-foreground">
              <Icon name="RefreshCw" size={12} />
              Updated just now
            </div>
          </div>

          {/* Filter pills with counts */}
          <div className="flex flex-wrap gap-2 mt-4">
            {FILTER_OPTIONS.map((f) => {
              const count = typeCounts[f.key] ?? 0;
              const isActive = filter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-caption font-medium transition-all"
                  style={{
                    background: isActive ? 'var(--color-primary)' : 'var(--color-muted)',
                    color:      isActive ? 'white' : 'var(--color-muted-foreground)',
                  }}
                >
                  {f.label}
                  {count > 0 && (
                    <span
                      className="flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-xs font-semibold"
                      style={{
                        background: isActive ? 'rgba(255,255,255,0.25)' : 'var(--color-border)',
                        color:      isActive ? 'white' : 'var(--color-foreground)',
                      }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-6 md:px-10 lg:px-12 py-8">
          {/* New items banner */}
          {!bannerDismissed && newItemCount > 0 && (
            <div
              className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg mb-6 border"
              style={{
                background: 'rgba(59,76,76,0.06)',
                borderColor: 'rgba(59,76,76,0.15)',
              }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ background: 'var(--color-primary)' }}
                />
                <span className="font-caption text-sm font-medium text-foreground">
                  {newItemCount} new item{newItemCount !== 1 ? 's' : ''} since your last visit
                </span>
              </div>
              <button
                onClick={() => setBannerDismissed(true)}
                className="flex-shrink-0"
              >
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
          )}

          {/* 2-column layout at lg+ */}
          <div className="lg:grid lg:grid-cols-[1fr_240px] lg:gap-6 lg:items-start">

            {/* Feed column */}
            <div>
              {grouped.length === 0 ? (
                <div className="card flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Icon name="Activity" size={36} color="var(--color-muted-foreground)" />
                  <p className="font-caption text-sm mt-3">No activity for this filter</p>
                </div>
              ) : (
                grouped.map((group) => (
                  <div key={group.date} className="mb-2">
                    {/* Date divider */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
                      <span className="font-caption text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap px-2">
                        {group.date}
                        <span className="ml-1.5 opacity-60">·</span>
                        <span className="ml-1.5 font-normal">{group.items.length}</span>
                      </span>
                      <div className="flex-1 h-px" style={{ background: 'var(--color-border)' }} />
                    </div>
                    {group.items.map((item) => (
                      <ActivityCard
                        key={item.id}
                        item={item}
                        isExpanded={expandedId === item.id}
                        onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                      />
                    ))}
                  </div>
                ))
              )}
            </div>

            {/* Right panel — visible at lg+ */}
            <div className="hidden lg:flex flex-col gap-4">

              {/* Stats strip */}
              <div className="card p-4">
                <p className="font-caption text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Today's Summary
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-caption text-xs text-muted-foreground">Total events today</span>
                    <span className="font-caption text-sm font-bold text-foreground">{todayCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-caption text-xs text-muted-foreground">Showing</span>
                    <span className="font-caption text-xs font-semibold" style={{ color: 'var(--color-primary)' }}>
                      {FILTER_OPTIONS.find((f) => f.key === filter)?.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Upcoming deadlines */}
              {deadlineItems.length > 0 && (
                <div className="card p-4">
                  <p className="font-caption text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Upcoming
                  </p>
                  <div className="space-y-2.5">
                    {deadlineItems.map((item) => {
                      const type = ACTIVITY_TYPES[item.type] ?? ACTIVITY_TYPES.system;
                      const deadlineTag = item.tags?.find((t) => DEADLINE_TAGS.includes(t));
                      return (
                        <div key={item.id} className="flex items-start gap-2.5">
                          <div
                            className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center mt-0.5"
                            style={{ background: type.bg }}
                          >
                            <Icon name={type.icon} size={12} color={type.color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-caption text-xs font-medium text-foreground leading-tight line-clamp-2">
                              {item.title}
                            </p>
                            {deadlineTag && (
                              <span
                                className="inline-block mt-1 px-1.5 py-0.5 rounded text-xs font-caption"
                                style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontSize: '0.65rem' }}
                              >
                                {deadlineTag}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quick nav */}
              <div className="card p-4">
                <p className="font-caption text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Quick Nav
                </p>
                <div className="space-y-1">
                  {QUICK_NAV.map((nav) => (
                    <button
                      key={nav.label}
                      onClick={() => navigate(nav.path)}
                      className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left hover:bg-muted transition-colors group"
                    >
                      <Icon name={nav.icon} size={14} color="var(--color-muted-foreground)" />
                      <span className="font-caption text-xs text-foreground group-hover:text-primary transition-colors">
                        {nav.label}
                      </span>
                      <Icon name="ArrowRight" size={11} color="var(--color-muted-foreground)" className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
